import { logInfo, logWarn } from './logger';

export interface TrackingInfoInterface {
  trackingEvents: Array<TrackingEventInterface>;
}

export interface TrackingEventInterface {
  beaconUrls: Array<string>;
  duration?: string;
  durationInSeconds?: string;
  eventId?: string;
  eventProgramDateTime?: string;
  eventType: string;
  startTime?: string;
  startTimeInSeconds?: number;
}

const appInstance = import.meta.env.VITE_APP_ENV;
const catchErrorPrefix = import.meta.env.VITE_APP_CATCH_ERROR_PREFIX;

function parseBeaconUrls(
  trackingInfo: TrackingInfoInterface,
  eventTypeString: string,
  ibUuid: string | null,
) {
  const beaconUrls = [];
  try {
    for (const trackingEvent of trackingInfo.trackingEvents) {
      if (trackingEvent && trackingEvent.eventType)
        if (trackingEvent && trackingEvent.eventType === eventTypeString) {
          if (trackingEvent && trackingEvent.beaconUrls.length > 0) {
            beaconUrls.push(...trackingEvent.beaconUrls);
          } else {
            const warningMsg7 = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - Missing some or all beaconUrls for trackingEvent ${eventTypeString}`;
            logWarn(warningMsg7);
            fetch(
              `${catchErrorPrefix}${appInstance}/beaconError?ib_uuid=${ibUuid}&warning=${encodeURIComponent(warningMsg7)}`,
            );
          }
        } else {
          const warningMsg8 = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - UnmatchedtrackingEvent ${eventTypeString} to ${trackingEvent.eventType}`;
          logWarn(warningMsg8);
          // fetch(
          //   `${catchErrorPrefix}${appInstance}/beaconError?warning=${encodeURIComponent(warningMsg8)}`,
          // );
        }
    }
    if (beaconUrls.length === 0) {
      const errorMsg = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - Could not find a matching eventType for ${eventTypeString} in the VAST data`;
      throw new Error(errorMsg);
    }
    return beaconUrls;
  } catch (error) {
    {
      logWarn((error as Error).message);
      fetch(
        `${catchErrorPrefix}${appInstance}/beaconError?ib_uuid=${ibUuid}&warning=${encodeURIComponent((error as Error).message)}`,
      );
      return null;
    }
  }
}

export default class BeaconManager {
  private requestUrls: Array<string>;

  constructor() {
    this.requestUrls = [];
  }

  // // In the beginning before we've loaded data we might not yet know the
  // // ibUuid from the JSON data (because it hasn't yet loaded) this attempts
  // // to extract it from a hopefully existing beacon in the data mostly for
  // // error reporting so we know what int break failed generally the beacon
  // // fires off just fine as long as it exists in the trackingInfo
  private extractIbUuidFromTrackingInfo(trackingInfo: TrackingInfoInterface): string | null {
    if (!trackingInfo || !trackingInfo.trackingEvents) {
      return null;
    }
    const startEvent = trackingInfo.trackingEvents.find(
      (event) =>
        event.eventType === 'start' &&
        event.beaconUrls &&
        event.beaconUrls.some((url) => url.includes('interactive-breaks.com')),
    );
    if (startEvent && startEvent.beaconUrls && startEvent.beaconUrls.length > 0) {
      const url =
        startEvent.beaconUrls.find((beaconUrl) => beaconUrl.includes('interactive-breaks.com')) ||
        startEvent.beaconUrls[0];
      try {
        // // Expected path structure:
        // // /vast/v1/ad/{ibUuid}/event/report/track/start
        const urlObj = new URL(url);
        // // pathSegments = [ '', 'vast', 'v1', 'ad', '${uuid}', 'event', 'report', 'track', 'start' ]
        const pathSegments = urlObj.pathname.split('/');
        // // /vast/v1/ad/{ibUuid}/...
        const ibUuidIndex = 4;

        if (pathSegments.length > ibUuidIndex) {
          const potentialUuid = pathSegments[ibUuidIndex];

          // // Basic validation for UUID format (e.g., 8-4-4-4-12 hex digits)
          if (
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(potentialUuid)
          ) {
            return potentialUuid;
          }
        }
      } catch (error) {
        // // Invalid URL or other parsing error
        const warningMsg = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - Error parsing beacon URL for ibUuid extraction: ${url}`;
        logWarn(warningMsg);
        logWarn(error);
        // fetch(
        //   `${catchErrorPrefix}${appInstance}/beaconError?warning=${encodeURIComponent(warningMsg)}`,
        // );
        return null;
      }
    }
    return null;
  }

  async update(ibUuid: string | null) {
    while (this.requestUrls.length > 0) {
      const requestUrl = this.requestUrls.pop(); // Process from the end
      if (requestUrl) {
        try {
          await fetch(requestUrl, { priority: 'low' });
        } catch (error) {
          const warningMsg = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - There was an error calling beacon ${requestUrl} with error: ${error}`;
          logWarn(warningMsg);
          fetch(
            `${catchErrorPrefix}${appInstance}/beaconError?ib_uuid=${ibUuid}&warning=${encodeURIComponent(warningMsg)}`,
          ).catch((e) => logWarn('Error logging beaconError:', e)); // Add catch for the error log fetch itself
        }
      }
    }
  }

  sendTrackingEvent(
    trackingInfo: TrackingInfoInterface,
    eventTypeString: string,
    ibUuid: string | null,
    refCode: number = -1,
  ) {
    // if trackingInfo doesn't even exist, we should report that first (probably won't have an ibUuid though)
    if (!trackingInfo) {
      const warningMsg1 = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - Unable to broadcast "${eventTypeString}" trackingEvent, as trackingInfo is not defined at this point`;

      logWarn(warningMsg1);

      fetch(
        `${catchErrorPrefix}${appInstance}/beaconError?ib_uuid=${ibUuid}&warning=${encodeURIComponent(warningMsg1)}`,
      );
      return;
    }

    let effectiveIbUuid = ibUuid;

    if (effectiveIbUuid == null) {
      effectiveIbUuid = this.extractIbUuidFromTrackingInfo(trackingInfo);
      if (effectiveIbUuid) {
        const infoMsg = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - Extracted ibUuid from trackingInfo: ${effectiveIbUuid}`;
        logInfo(infoMsg);
      } else {
        const warningMsg = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - ibUuid was not provided and could not be extracted from trackingInfo. Error reporting may be affected.`;
        logWarn(warningMsg);
      }
    }

    if (typeof refCode !== 'number') {
      const warningMsg2 = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - Malformed ref code passed to sendTrackingEvent`;

      logWarn(warningMsg2);

      fetch(
        `${catchErrorPrefix}${appInstance}/beaconError?ib_uuid=${effectiveIbUuid}&warning=${encodeURIComponent(warningMsg2)}`,
      );
      refCode = -1;
    }

    if (trackingInfo && trackingInfo.trackingEvents && trackingInfo.trackingEvents.length > 0) {
      const beaconUrls = parseBeaconUrls(trackingInfo, eventTypeString, effectiveIbUuid);

      logInfo(`${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - beaconUrls`, beaconUrls);

      if (beaconUrls) {
        for (let beaconUrl of beaconUrls) {
          if (eventTypeString == 'clickTracking') {
            if (beaconUrl.indexOf('[BUTTON_NAME]') !== -1) {
              beaconUrl = beaconUrl.replace('[BUTTON_NAME]', `${refCode}`);
            }
          } else if (eventTypeString == 'nasbUiError') {
            if (refCode == -1) {
              const warningMsg3 = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - Broadcasting an error type event without a valid ref code. Error has an unknown origin.`;

              logWarn(warningMsg3);

              fetch(
                `${catchErrorPrefix}${appInstance}/beaconError?ib_uuid=${effectiveIbUuid}&warning=${encodeURIComponent(warningMsg3)}`,
              );
            }

            if (beaconUrl.indexOf('[REFCODE]') !== -1) {
              beaconUrl = beaconUrl.replace('[REFCODE]', `${refCode}`);
            }

            if (beaconUrl.indexOf('[ERRORCODE]') !== -1) {
              beaconUrl = beaconUrl.replace('[ERRORCODE]', '600');
            } else {
              const warningMsg4 = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - Cannot broadcast "error" trackingEvent with errorCode 600, malformed beaconUrl: ${beaconUrl}`;

              logWarn(warningMsg4);

              fetch(
                `${catchErrorPrefix}${appInstance}/beaconError?ib_uuid=${effectiveIbUuid}&warning=${encodeURIComponent(warningMsg4)}`,
              );
              continue;
            }
          }

          const infoMsg = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - Broadcasting tracking eventType "${eventTypeString}" by GETting ${beaconUrl}`;

          logInfo(infoMsg);

          // fetch(
          //   `${catchErrorPrefix}${appInstance}/beaconError?info=${encodeURIComponent(infoMsg)}`,
          // );
          this.requestUrls.push(beaconUrl);
        }
      } else {
        const warningMsg5 = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - No beaconUrls were provided for ${eventTypeString} in the VAST data`;

        logWarn(warningMsg5);

        fetch(
          `${catchErrorPrefix}${appInstance}/beaconError?ib_uuid=${effectiveIbUuid}&warning=${encodeURIComponent(warningMsg5)}`,
        );
        return;
      }
      this.update(effectiveIbUuid);
    } else {
      const warningMsg6 = `${import.meta.env.VITE_GLOBAL_APPLICATION_NAME} - The trackingInfo object does not contain valid trackingEvent data`;

      logWarn(warningMsg6);

      fetch(
        `${catchErrorPrefix}${appInstance}/beaconError?ib_uuid=${effectiveIbUuid}&eventType=${eventTypeString}&warning=${encodeURIComponent(warningMsg6)}&trackingInfo=${encodeURIComponent(JSON.stringify(trackingInfo.trackingEvents))}`,
      );
      return;
    }
  }
}
